import React, { useState, useEffect } from "react";
import { Calendar, Award, Users, Wrench, ChevronLeft, ChevronRight } from "lucide-react";

const AboutSection = () => {
  const [currentImage, setCurrentImage] = useState(0);

  const stats = [
    { icon: Calendar, label: "Fondata nel", value: "2017" },
    { icon: Award, label: "Anni di esperienza", value: "7+" },
    { icon: Users, label: "Clienti soddisfatti", value: "14.000+" },
    { icon: Wrench, label: "Progetti completati", value: "18.000+" },
  ];

  const projectImages = [
    {
      url: "/images/vwd_foto1.jpeg",
      title: "Bagno Moderno Elegante",
      description: "Rivestimenti in marmo beige con doppio lavabo sospeso e design contemporaneo"
    },
    {
      url: "/images/hero2.jpeg",
      title: "Scala Artistica con Illuminazione LED",
      description: "Scala in marmo con corrimano decorativo e illuminazione scenografica arancione"
    },
    {
      url: "/images/about3.jpg",
      title: "Lavorazione Specializzata",
      description: "Dettagli architettonici personalizzati con decorazioni murali artistiche"
    },
    {
      url: "/images/hero4.jpeg",
      title: "Arco Decorativo Artistico",
      description: "Arco con decorazioni classiche, illuminazione calda e dettagli architettonici storici"
    },
    {
      url: "/images/hero5.jpeg",
      title: "Bagno Luxury con Travi a Vista",
      description: "Bagno di lusso con travi in legno, marmi pregiati e doppi sanitari sospesi"
    }
  ];

  // Auto-slide ogni 4 secondi
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % projectImages.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [projectImages.length]);

  const goToPrevious = () => {
    setCurrentImage((prev) => (prev - 1 + projectImages.length) % projectImages.length);
  };

  const goToNext = () => {
    setCurrentImage((prev) => (prev + 1) % projectImages.length);
  };

  return (
    <section id="about" className="py-20 lg:py-32 bg-gray-50 scroll-mt-24">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                La nostra storia e la passione per le soluzioni che{" "}
                <span className="text-blue-600">trasformano la tua casa</span>
              </h2>
            </div>

            <div className="space-y-6 text-gray-700 text-lg leading-relaxed text-justify">
              <p>
                <strong>Fondata nel 2017</strong>, Star Italia nasce dall'idea di unire esperienza e 
                innovazione nel settore delle ristrutturazioni, con un focus particolare sulla 
                trasformazione e valorizzazione dei bagni. Da allora, l'azienda si è consolidata 
                grazie alla cura artigianale, all'attenzione ai dettagli e alla capacità di offrire 
                soluzioni personalizzate che uniscono funzionalità e stile, trasformando ogni spazio 
                in un ambiente unico e moderno.
              </p>

              <p>
                <strong>Da oltre 3 generazioni ristrutturiamo case con passione, professionalità e competenza.</strong>
                Star Italia oggi è un punto di riferimento nel settore degli infissi, 
                serramenti e soluzioni per la casa, con un'esperienza pluriennale e un impegno costante 
                nell'innovazione, qualità e servizio al cliente. Offriamo prodotti su misura e tecnologie 
                all'avanguardia per migliorare comfort, sicurezza e design degli ambienti abitativi.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-6 pt-8">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="text-center p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <stat.icon className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Dynamic Image Slider */}
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl relative">
              {/* Images */}
              {projectImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                    index === currentImage
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-105'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay con informazioni */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <h4 className="text-lg font-bold mb-2">{image.title}</h4>
                      <p className="text-sm text-gray-200">{image.description}</p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Navigation Arrows */}
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 rounded-full transition-all duration-300 hover:scale-110"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 rounded-full transition-all duration-300 hover:scale-110"
              >
                <ChevronRight className="h-5 w-5" />
              </button>

              {/* Dot Indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {projectImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentImage
                        ? 'bg-blue-400 scale-125'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                  />
                ))}
              </div>

              {/* Image Counter */}
              <div className="absolute top-4 right-4 bg-black/30 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                {currentImage + 1} / {projectImages.length}
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-600 rounded-full opacity-10"></div>
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-amber-400 rounded-full opacity-10"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;